import * as React from 'react';
import type { HTMLAttributes, CommonlyExcludedHTMLAttributeNames, Breakpoint } from 'types/utils';
import { connect } from 'react-redux';
import type { RootReducer } from 'store/root-reducer';
import { Typography } from 'fronton-react';
import {
  LARGE_DESK_SCREEN,
  MEDIUM_DESK_SCREEN,
  TABLET_SCREEN,
  MOBILE_SCREEN,
} from 'utils/internal-specifics/media-queries';
import { addMediaQueryHandlers } from 'utils/add-media-query-handlers';
import { getPrimaryPriceUnitName } from 'features/product-info';
import { useTranslation } from 'react-i18next';

type AcceptedHTMLAttributes = Omit<HTMLAttributes, CommonlyExcludedHTMLAttributeNames>;
type PdpFloorCoveringsDescriptionProps = AcceptedHTMLAttributes;

type FloorCoveringsDescriptionMappedState = {
  /** @example 'м²' */
  unitName: string;
};
type FloorCoveringsDescriptionConnectedProps = FloorCoveringsDescriptionMappedState;

/**
 * @internal Used only for tests
 */
export const FloorCoveringsDescription: React.FC<
  PdpFloorCoveringsDescriptionProps & FloorCoveringsDescriptionConnectedProps & { breakpoint: Breakpoint }
> = ({ breakpoint, unitName }) => {
  const { t } = useTranslation();
  return (
    <Typography
      data-qa="floor-coverings-description_mf-pdp"
      variant={breakpoint === 'mobile' ? 's' : 'm'}
      size="body_short"
      color="text-secondary"
    >
      {t('product-info.sales-in')} {unitName}
    </Typography>
  );
};

const FloorCoveringsDescriptionWithMediaEffect: React.FC<
  PdpFloorCoveringsDescriptionProps & FloorCoveringsDescriptionConnectedProps
> = ({ ...props }) => {
  const [breakpoint, setBreakpoint] = React.useState<Breakpoint>('large');
  React.useEffect(
    () =>
      addMediaQueryHandlers(
        [LARGE_DESK_SCREEN, MEDIUM_DESK_SCREEN, TABLET_SCREEN, MOBILE_SCREEN],
        ['large', 'medium', 'tablet', 'mobile'],
        'large',
        setBreakpoint,
      ),
    [],
  );
  return <FloorCoveringsDescription breakpoint={breakpoint} {...props} />;
};

function mapStateToProps(state: RootReducer): FloorCoveringsDescriptionMappedState {
  const unitName = getPrimaryPriceUnitName(state) ?? 'м²';

  return {
    unitName,
  };
}

export const PdpFloorCoveringsDescription = connect(mapStateToProps)(
  FloorCoveringsDescriptionWithMediaEffect,
);
